import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import {
  ArrowRight, BarChart3, CheckCircle2, Gauge, GitPullRequest, KeyRound, LineChart,
  Lock, Search, ShieldCheck, Target, Workflow,
} from "lucide-react";

const STEPS = [
  { icon: Search, title: "Connect Google", text: "Search Console and GA4 provide the evidence. Real data, never guesses." },
  { icon: Gauge, title: "Engine analyses", text: "A deterministic SEO engine detects striking-distance queries, CTR gaps, decay, cannibalisation and technical issues." },
  { icon: Target, title: "Prioritised opportunities", text: "Every opportunity is scored, explained and backed by evidence you can inspect." },
  { icon: CheckCircle2, title: "You approve", text: "Nothing meaningful changes without a human decision. Approve & Run builds the task for you." },
  { icon: KeyRound, title: "Your AI executes", text: "Bring your own AI key. Scoped tasks, protected areas, validation gates." },
  { icon: GitPullRequest, title: "Preview & PR", text: "Changes land as a branch and pull request. Preview before anything ships." },
  { icon: LineChart, title: "Measure", text: "7 / 14 / 28-day observation windows after each deployment. Then the cycle repeats." },
];

const FEATURES = [
  { title: "Striking-distance detection", text: "Queries ranking 8–20 with real impressions - the fastest wins available." },
  { title: "CTR opportunity engine", text: "Strong rankings with weak snippets, quantified against expected CTR curves." },
  { title: "Content decay alerts", text: "28-day and quarterly windows with noise protection. No false alarms." },
  { title: "Cannibalisation review", text: "Competing pages classified - never auto-merged, always human-reviewed." },
  { title: "Technical SEO audit", text: "Crawler-driven inventory: titles, canonicals, indexability, orphans, depth." },
  { title: "Safe AI automation", text: "Explicit scopes, protected areas, validation gates, production approval." },
];

const FAQS = [
  { q: "Do you guarantee rankings?", a: "No - and you should distrust anyone who does. We surface evidence from Google, prioritise deterministically, and measure observed outcomes after each change. Search results are ultimately decided by Google." },
  { q: "Who pays for the AI?", a: "You bring your own AI provider key (OpenAI first, more coming). Your subscription covers the SEO platform; AI inference is billed by your provider at their rates." },
  { q: "Is my AI key safe?", a: "Keys are encrypted server-side before storage and never returned to the browser after submission. You only ever see a masked form like ••••••••1234." },
  { q: "Can AI change my site without asking?", a: "No. Every task requires explicit approval, runs inside an allowed-changes scope with protected areas, passes a validation gate, and production deployment requires a second human approval." },
  { q: "Does it work without GitHub?", a: "Yes. You still get the full analysis, recommendations and exportable change plans with copy, metadata and schema suggestions." },
  { q: "Does it work without an AI key?", a: "Yes. The deterministic engine - audits, opportunities, striking distance, CTR gaps, decay, cannibalisation - works entirely without AI." },
];

export default function Landing() {
  const [plans, setPlans] = useState([]);
  useEffect(() => {
    api.get("/plans").then((r) => setPlans(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-30 border-b bg-background/90 backdrop-blur">
        <div className="mx-auto flex h-14 max-w-[1120px] items-center justify-between px-4 sm:px-6">
          <div className="flex items-center gap-2">
            <Gauge className="h-5 w-5 text-primary" />
            <span className="text-sm font-bold tracking-tight">SIX9 SEO</span>
          </div>
          <div className="flex items-center gap-2">
            <Button asChild variant="ghost" size="sm" data-testid="landing-login-button">
              <Link to="/login">Log in</Link>
            </Button>
            <Button asChild size="sm" data-testid="landing-get-started-button">
              <Link to="/signup">Get started</Link>
            </Button>
          </div>
        </div>
      </header>

      <section className="relative overflow-hidden">
        <div className="pointer-events-none absolute inset-0" style={{ backgroundImage: "radial-gradient(600px circle at 20% 10%, rgba(31,75,153,0.08), transparent 55%), radial-gradient(500px circle at 80% 0%, rgba(15,23,42,0.05), transparent 50%)" }} />
        <div className="mx-auto max-w-[1120px] px-4 py-16 sm:px-6 sm:py-24">
          <Badge variant="outline" className="mb-4 border-primary/20 bg-primary/5 text-primary">Evidence-first SEO automation</Badge>
          <h1 data-testid="landing-hero-headline" className="max-w-3xl text-4xl font-bold tracking-tight sm:text-5xl lg:text-6xl">
            Know what to fix. Approve it. Let AI do the work.
          </h1>
          <p className="mt-5 max-w-2xl text-base text-muted-foreground sm:text-lg">
            Google provides the data. Our engine finds the opportunity. Your AI executes the approved work.
            A multi-tenant SEO intelligence platform with human control at every meaningful step.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button asChild size="lg" data-testid="landing-hero-cta">
              <Link to="/signup">Start free analysis <ArrowRight className="ml-1.5 h-4 w-4" /></Link>
            </Button>
            <Button asChild size="lg" variant="outline">
              <Link to="/login">View your dashboard</Link>
            </Button>
          </div>
          <div className="mt-12 grid grid-cols-1 gap-3 sm:grid-cols-3">
            {[{ icon: Search, t: "Google = evidence", d: "Search Console + GA4 are the only source of performance truth." },
              { icon: Workflow, t: "Engine = decisions", d: "Deterministic scoring and prioritisation. Explainable, configurable." },
              { icon: ShieldCheck, t: "Human = authority", d: "Approval required for changes and again before production." }].map((x) => (
              <Card key={x.t} className="shadow-none">
                <CardContent className="flex items-start gap-3 p-4">
                  <x.icon className="mt-0.5 h-5 w-5 text-primary" />
                  <div>
                    <div className="text-sm font-semibold">{x.t}</div>
                    <div className="text-xs text-muted-foreground">{x.d}</div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t bg-card">
        <div className="mx-auto max-w-[1120px] px-4 py-16 sm:px-6">
          <h2 className="text-2xl font-bold tracking-tight">How it works</h2>
          <p className="mt-1 text-sm text-muted-foreground">The continuous improvement loop, end to end.</p>
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {STEPS.map((s, i) => (
              <div key={s.title} className="rounded-lg border bg-background p-4">
                <div className="flex items-center gap-2">
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 text-xs font-bold text-primary">{i + 1}</span>
                  <s.icon className="h-4 w-4 text-muted-foreground" />
                </div>
                <div className="mt-2 text-sm font-semibold">{s.title}</div>
                <div className="mt-1 text-xs text-muted-foreground">{s.text}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t">
        <div className="mx-auto max-w-[1120px] px-4 py-16 sm:px-6">
          <div className="grid gap-8 lg:grid-cols-2">
            <div>
              <h2 className="text-2xl font-bold tracking-tight">Bring your own AI key</h2>
              <p className="mt-2 text-sm text-muted-foreground">
                Connect OpenAI today (provider abstraction ready for more). Your key is encrypted server-side,
                never shown again, and AI usage is billed by your provider - not marked up by us.
              </p>
              <div className="mt-4 flex items-center gap-2 rounded-md border bg-card p-3">
                <Lock className="h-4 w-4 text-emerald-600" />
                <span className="font-mono text-sm">OpenAI · Connected · ••••••••1234</span>
              </div>
            </div>
            <div>
              <h2 className="text-2xl font-bold tracking-tight">Safe automation, by design</h2>
              <ul className="mt-2 space-y-2 text-sm text-muted-foreground">
                {["Explicit allowed-changes scopes per task", "Protected areas: pricing, legal, brand, auth, secrets",
                  "Deterministic validation gates before preview", "Pull requests - never auto-merged",
                  "Human production approval, always", "No doorway pages, no fake local content, no mass publishing"].map((x) => (
                  <li key={x} className="flex items-start gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />{x}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="border-t bg-card">
        <div className="mx-auto max-w-[1120px] px-4 py-16 sm:px-6">
          <h2 className="text-2xl font-bold tracking-tight">SEO intelligence, deterministic first</h2>
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {FEATURES.map((f) => (
              <div key={f.title} className="rounded-lg border bg-background p-4">
                <BarChart3 className="h-4 w-4 text-primary" />
                <div className="mt-2 text-sm font-semibold">{f.title}</div>
                <div className="mt-1 text-xs text-muted-foreground">{f.text}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t" id="pricing">
        <div className="mx-auto max-w-[1120px] px-4 py-16 sm:px-6">
          <h2 className="text-2xl font-bold tracking-tight">Pricing</h2>
          <p className="mt-1 text-sm text-muted-foreground">Final pricing to be announced. Plans and limits below.</p>
          <div className="mt-8 grid gap-4 sm:grid-cols-3">
            {plans.map((p) => (
              <Card key={p.id} className={`shadow-none ${p.id === "pro" ? "border-primary" : ""}`} data-testid={`pricing-plan-${p.id}`}>
                <CardContent className="p-5">
                  <div className="text-sm font-bold">{p.name}</div>
                  <div className="mt-1 text-2xl font-bold tabular-nums">
                    {p.price_monthly === 0 ? "$0" : "TBA"}
                    <span className="text-xs font-normal text-muted-foreground"> /month</span>
                  </div>
                  <div className="text-xs text-muted-foreground">{p.price_note}</div>
                  <ul className="mt-4 space-y-1.5">
                    {(p.features || []).map((f) => (
                      <li key={f} className="flex items-start gap-1.5 text-xs text-muted-foreground">
                        <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600" />{f}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t bg-card">
        <div className="mx-auto max-w-[760px] px-4 py-16 sm:px-6">
          <h2 className="text-2xl font-bold tracking-tight">Frequently asked questions</h2>
          <Accordion type="single" collapsible className="mt-6">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`q${i}`}>
                <AccordionTrigger className="text-left text-sm font-semibold">{f.q}</AccordionTrigger>
                <AccordionContent className="text-sm text-muted-foreground">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <footer className="border-t">
        <div className="mx-auto flex max-w-[1120px] flex-col items-center justify-between gap-2 px-4 py-8 text-xs text-muted-foreground sm:flex-row sm:px-6">
          <span>SIX9 SEO — evidence-first SEO automation.</span>
          <span>No guaranteed rankings. Google decides search results.</span>
        </div>
      </footer>
    </div>
  );
}
